/**
 * SortableImageCard - Wraps ImageCardContent with dnd-kit sortable behavior.
 *
 * Handles drag state, pending indicators, and change markers.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trash2, Undo2 } from 'lucide-react';
import { ImageCardContent } from './ImageCardContent';
import { cn } from '@/lib/utils';
import type { ProductAsset } from '@/types/products';
import type { AssetValidationError } from '@/hooks/useChannelValidation';
import type { DragItemData } from './types';

export interface SortableImageCardProps {
  sortableId: string;
  asset: ProductAsset;
  index: number;
  isDragging: boolean;
  onClick: () => void;
  channelKey: string;
  channelId: string | null;
  onInitialize: () => void;
  isChanged: boolean;
  isPendingUpload: boolean;
  validationErrors?: AssetValidationError[];
  onDeleteSlot?: (channelId: string, linkId: string, slot: number) => void;
  isPendingDelete?: boolean;
  isCascadeDelete?: boolean;
  onUndoDelete?: (channelId: string, linkId: string) => void;
  productId?: string;
  onAltTextGenerated?: (assetId: string, text: string) => void;
}

export function SortableImageCard({
  sortableId,
  asset,
  index,
  isDragging,
  onClick,
  channelKey,
  channelId,
  onInitialize,
  isChanged,
  isPendingUpload,
  validationErrors = [],
  onDeleteSlot,
  isPendingDelete = false,
  isCascadeDelete = false,
  onUndoDelete,
  productId,
  onAltTextGenerated,
}: SortableImageCardProps) {
  const isPendingCopy = asset.link_id.startsWith('pending-') || asset.is_pending === true;
  const isPending = isPendingCopy || isPendingUpload;
  const hasErrors = validationErrors.length > 0;
  const isValid = !hasErrors && !isPending && !isChanged;

  const { attributes, listeners, setNodeRef, transform, transition, isOver } = useSortable({
    id: sortableId,
    data: { asset, channelKey, channelId } as DragItemData,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : isPendingDelete ? 0.5 : isPending ? 0.85 : 1,
  };

  const handleDragStart = useCallback(() => {
    onInitialize();
  }, [onInitialize]);

  return (
    <div ref={setNodeRef} style={style} onPointerDown={handleDragStart}>
      <div
        className={cn(
          'group/card relative',
          isOver && 'ring-2 ring-primary-500 rounded-lg',
          isPendingDelete && 'border-2 border-dashed border-red-400 rounded-lg',
          !isPendingDelete && hasErrors && 'ring-2 ring-red-500 rounded-lg',
          !isPendingDelete && isValid && 'ring-2 ring-emerald-400 rounded-lg',
          !isPendingDelete && isChanged && !isPending && !hasErrors && !isValid && 'ring-2 ring-amber-400 rounded-lg',
          !isPendingDelete && isPending && 'ring-2 ring-dashed ring-emerald-500 rounded-lg'
        )}
      >
        <ChangeIndicator isChanged={isChanged} isPending={isPending} isPendingDelete={isPendingDelete} />
        {isPendingDelete && channelId && onUndoDelete ? (
          <UndoDeleteButton onClick={() => onUndoDelete(channelId, asset.link_id)} />
        ) : !isPending && !isPendingDelete && channelId && onDeleteSlot ? (
          <DeleteButton onClick={() => onDeleteSlot(channelId, asset.link_id, index + 1)} />
        ) : null}
        <ImageCardContent
          asset={asset}
          index={index}
          onClick={onClick}
          dragHandleProps={{ ...attributes, ...listeners }}
          productId={productId}
          onAltTextGenerated={onAltTextGenerated}
        />
        {isPendingDelete && <PendingDeleteOverlay />}
        {isPendingDelete && isCascadeDelete && <CascadeDeleteWarning />}
        {hasErrors && !isPendingDelete && <ValidationErrorList errors={validationErrors} />}
      </div>
    </div>
  );
}

/** Visual indicators for changed/pending/delete state. */
function ChangeIndicator({ isChanged, isPending, isPendingDelete }: { isChanged: boolean; isPending: boolean; isPendingDelete: boolean }) {
  const { t } = useTranslation()
  if (isPendingDelete) {
    return (
      <div className="absolute -top-2 -right-2 z-20 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-semibold shadow-sm" title={t('productImages.deleteMarked')}>
        <Trash2 className="h-3 w-3 inline mr-0.5" />{t('productImages.pendingDelete')}
      </div>
    );
  }
  if (isPending) {
    return (
      <div
        className="absolute -top-2 -right-2 z-20 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-sm"
        title={t('products.newImageAdded')}
      >
        {t('products.newBadge')}
      </div>
    );
  }
  if (isChanged) {
    return (
      <div
        className="absolute -top-1.5 -right-1.5 z-20 w-4 h-4 rounded-full bg-amber-400 border-2 border-white dark:border-neutral-900 shadow-sm"
        title={t('products.pendingChanges')}
      />
    );
  }
  return null;
}

/** Delete button shown on hover for channel slot images. */
function DeleteButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'absolute -top-2 -right-2 z-30',
        'flex items-center justify-center w-7 h-7 rounded-full',
        'bg-red-500 hover:bg-red-600 text-white shadow-md',
        'opacity-0 group-hover/card:opacity-100 transition-opacity',
        'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-400'
      )}
      title={t('products.removeFromChannel')}
      aria-label={t('products.removeFromChannel')}
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

/** Overlay shown on images marked for deletion. */
function PendingDeleteOverlay() {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-900/30 rounded-lg pointer-events-none"><Trash2 className="h-8 w-8 text-red-400 opacity-80" /></div>
  );
}

/** Warning text for cascade deletes (global channel). */
function CascadeDeleteWarning() {
  const { t } = useTranslation();
  return (
    <div className="mt-1 text-2xs leading-tight text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded text-center">
      {t('productImages.cascadeDeleteWarning')}
    </div>
  );
}

/** Undo button shown on hover for images pending deletion. */
function UndoDeleteButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'absolute -top-2 -right-2 z-30 flex items-center justify-center w-7 h-7 rounded-full',
        'bg-primary-500 hover:bg-primary-600 text-white shadow-md',
        'opacity-0 group-hover/card:opacity-100 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-400'
      )}
      title={t('productImages.undoDelete')}
      aria-label={t('productImages.undoDelete')}
    >
      <Undo2 className="h-3.5 w-3.5" />
    </button>
  );
}

/** Per-image validation errors shown below the card. */
function ValidationErrorList({ errors }: { errors: AssetValidationError[] }) {
  const { t } = useTranslation()
  return (
    <div className="mt-1 space-y-0.5">
      {errors.slice(0, 3).map((err, i) => (
        <div
          key={i}
          className="text-2xs leading-tight text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded"
          title={err.expected ? t('products.validationExpectedActual', { expected: err.expected, actual: err.actual }) : undefined}
        >
          {err.message}
        </div>
      ))}
    </div>
  );
}
